import { FunctionComponent, useContext, useEffect, useState } from "react";
import {
  ProductionTaskEnum,
  ProductionTaskStateEnum,
  ProductionTaskType,
  ProductionVMType,
  UpdateProductionTaskType,
} from "types/ProductionType";
import PartStateButton from "components/Buttons/PartStateButton";
import TaskTableRow from "../TaskTableRow";
import { apiUpdateProductionTask } from "util/network/Productions";
import { useToast } from "contexts/ToastContext";
import AppStateContext from "contexts/AppStateContext";

export interface ProductionPartState {
  state: ProductionTaskStateEnum | undefined;
  type: ProductionTaskEnum | undefined;
}

type PropsType = {
  project: ProductionVMType;
};

const CamRow: FunctionComponent<PropsType> = ({ project }) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const [taskStatus, setTaskStatus] = useState<ProductionPartState>({
    type: undefined,
    state: undefined,
  });
  const [tasks, setTasks] = useState<ProductionTaskType[]>(project.tasks);

  useEffect(() => {
    const camTask = project.tasks.find((tsk) => tsk.name === "cam");
    const reviewTask = project.tasks.find((tsk) => tsk.name === "review");

    if (
      reviewTask &&
      reviewTask.state === ProductionTaskStateEnum.DONE &&
      camTask
    ) {
      setTaskStatus({
        type: ProductionTaskEnum.CAM,
        state: camTask.state as ProductionTaskStateEnum,
      });
    } else if (camTask && camTask.state === ProductionTaskStateEnum.DONE) {
      setTaskStatus({
        type: ProductionTaskEnum.CAM,
        state: ProductionTaskStateEnum.DONE,
      });
    } else if (camTask && camTask.state === ProductionTaskStateEnum.ACTIVE) {
      setTaskStatus({
        type: ProductionTaskEnum.CAM,
        state: ProductionTaskStateEnum.ACTIVE,
      });
    } else if (
      reviewTask &&
      reviewTask.state === ProductionTaskStateEnum.ACTIVE
    ) {
      setTaskStatus({
        type: ProductionTaskEnum.REVIEW,
        state: ProductionTaskStateEnum.ACTIVE,
      });
    } else if (
      reviewTask &&
      reviewTask.state === ProductionTaskStateEnum.TODO
    ) {
      setTaskStatus({
        type: ProductionTaskEnum.REVIEW,
        state: ProductionTaskStateEnum.TODO,
      });
    } else {
      setTaskStatus({ type: undefined, state: undefined });
    }
  }, [project.tasks]);

  const handleTaskUpdate = (changes: Partial<ProductionTaskType>) => {
    if (project) {
      const task = project.tasks.find((task) => task.id === changes.id);
      if (!task) {
        return;
      }
      const updatedTask = {
        ...task,
        ...changes,
      } as ProductionTaskType;

      setTasks((prevTasks) => {
        return prevTasks.map((prevTask) => {
          if (prevTask.id === changes.id) {
            return {
              ...prevTask,
              ...changes,
            };
          } else if (changes.state) {
            if (
              changes.state === ProductionTaskStateEnum.DONE ||
              changes.state === ProductionTaskStateEnum.ACTIVE
            ) {
              if (prevTask.priority < updatedTask.priority) {
                return {
                  ...prevTask,
                  state: ProductionTaskStateEnum.DONE,
                };
              } else {
                return prevTask;
              }
            } else if (updatedTask.state === ProductionTaskStateEnum.TODO) {
              if (prevTask.priority > updatedTask.priority) {
                return {
                  ...prevTask,
                  state: ProductionTaskStateEnum.TODO,
                };
              } else {
                return prevTask;
              }
            } else {
              return prevTask;
            }
          } else {
            return prevTask;
          }
        });
      });
      let id = Number(changes.id);
      if (isNaN(id)) {
        return;
      }
      const command = {
        id: id,
        name: changes.name,
        description: changes.description,
        state: changes.state,
        priority: changes.priority,
        progress: changes.progress,
        progressMax: changes.progressMax,
        deadline: changes.deadline,
        leadTime: changes.leadTime,
        workTime: changes.workTime,
        users: changes.users?.map((user) => user.id),
        machines: changes.machines?.map((machine) => machine.id),
      } as UpdateProductionTaskType;
      apiUpdateProductionTask(token, id, command)
        .then((res) => {
          if (res) {
            addToast({
              type: "success",
              message: "Task updated",
              time: 1000,
            });
            if (updatedTask.state === ProductionTaskStateEnum.DONE) {
              const tasksDone = tasks.every(
                (task) =>
                  task.state === ProductionTaskStateEnum.DONE ||
                  task.id === updatedTask.id
              );
            }
          }
        })
        .catch((error) => {
          addToast({
            type: "error",
            message: "Failed to update task",
            keep: true,
          });
          console.error("Failed to update task:", error);
        });
    }
  };

  const getTaskToDisplay = () => {
    const camTaskIndex = project.tasks.findIndex(
      (tsk) => tsk.name === "cam" || tsk.description?.includes("cam")
    );
    if (project.productId == 5771) console.log(project.tasks);

    const orderedTasks = project.tasks.sort((a, b) =>
      a.priority > b.priority ? 1 : a.priority < b.priority ? -1 : 0
    );

    if (camTaskIndex === -1) {
      return (
        orderedTasks.find((tsk) => tsk.state != ProductionTaskStateEnum.DONE) ||
        orderedTasks.reduce(
          (prev, curr) => (prev.priority < curr.priority ? curr : prev),
          project.tasks[0]
        )
      );
    }

    const camTask = orderedTasks[camTaskIndex];

    const beforeTask =
      camTaskIndex > 0 ? orderedTasks[camTaskIndex - 1] : undefined;

    if (beforeTask?.state == ProductionTaskStateEnum.DONE) {
      return camTask;
    }

    return beforeTask ?? camTask;

    // if (camTask.state === ProductionTaskStateEnum.DONE) {
    //   return null;
    // }

    // Check the tasks before "cam"
    for (let i = 0; i < camTaskIndex; i++) {
      const task = orderedTasks[i];
      if (
        task.state === ProductionTaskStateEnum.TODO ||
        task.state === ProductionTaskStateEnum.ACTIVE
      ) {
        return task;
      }
    }

    // Check the "cam" task
    if (
      camTask.state === ProductionTaskStateEnum.TODO ||
      camTask.state === ProductionTaskStateEnum.ACTIVE
    ) {
      return camTask;
    }

    return camTask;
  };

  const taskToDisplay = getTaskToDisplay();

  return (
    <>
      {taskToDisplay && (
        <TaskTableRow
          task={taskToDisplay}
          key={taskToDisplay.id}
          cam
          project={project}
          buttonComponent={
            <PartStateButton
              key={taskToDisplay.id}
              type={taskToDisplay.name as ProductionTaskEnum}
              state={taskToDisplay.state}
              project={project}
              onChange={handleTaskUpdate}
              task={taskToDisplay}
              title={
                taskToDisplay.description != ""
                  ? taskToDisplay.description
                  : undefined
              }
            />
          }
          taskStatus={taskStatus}
        />
      )}
    </>
  );
};
export default CamRow;
